import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  List,
  ReferenceInput,
  SelectInput,
  ReferenceField,
  TextField,
} from "react-admin";
import { hostname } from "../providers/api";
import { useTokenOps } from "./TokenContext";

const ACTIVE_DB_VERSION_STORAGE_KEY = "bcome-admin-active-db-version-id";
const ACTIVE_BCOME_VERSION_STORAGE_KEY = "bcome-admin-active-bcome-version-id";

export const formatBcomeVersionLabel = (record) => {
  if (!record) return "";
  const lca = record.lcaMethodologyVersion?.name || "";
  const db = record.databaseVersion?.name || "";
  const circularity =
    record.circularityVersion?.circularity_version_name || "";
  const desc = record.description || "";
  const concat = [
    lca ? `LCA ${lca}` : "",
    db ? `DB ${db}` : "",
    circularity ? `Circularity ${circularity}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  if (!concat && !desc) return record.id || "";
  if (concat && desc) return `${concat}  (${desc})`;
  return concat || desc;
};

export const useActiveDbVersionId = () => {
  const tokenOps = useTokenOps();
  const [activeId, setActiveId] = useState(() =>
    localStorage.getItem(ACTIVE_DB_VERSION_STORAGE_KEY)
  );

  useEffect(() => {
    const token = tokenOps.account?.accessToken;
    if (!token) return;
    axios
      .get(`${hostname}/database-version`, {
        params: { filter: "isActive||$eq||true", limit: 1 },
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + token,
        },
      })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.data;
        const id = list && list[0] && list[0].id;
        if (id) {
          setActiveId(id);
          localStorage.setItem(ACTIVE_DB_VERSION_STORAGE_KEY, id);
        }
      })
      .catch((e) => console.log("[DbVersion] active fetch failed", e));
  }, [tokenOps.account?.accessToken]);

  return activeId;
};

export const DbVersionFilterInput = (props) => (
  <ReferenceInput
    {...props}
    label="DB Version"
    source="databaseVersionId"
    reference="databaseVersion"
    sort={{ field: "name", order: "ASC" }}
    perPage={100}
    allowEmpty
  >
    <SelectInput optionText="name" emptyText="All" emptyValue="" />
  </ReferenceInput>
);

export const DbVersionInput = (props) => {
  const activeId = useActiveDbVersionId();
  return (
    <ReferenceInput
      {...props}
      label="DB Version"
      source="databaseVersionId"
      reference="databaseVersion"
      sort={{ field: "name", order: "ASC" }}
      perPage={100}
      defaultValue={activeId}
    >
      <SelectInput optionText="name" />
    </ReferenceInput>
  );
};

export const DbVersionField = (props) => (
  <ReferenceField
    {...props}
    source="databaseVersionId"
    reference="databaseVersion"
    link={false}
  >
    <TextField source="name" />
  </ReferenceField>
);

DbVersionField.defaultProps = {
  label: "DB Version",
  addLabel: true,
  source: "databaseVersionId",
};

const VersionedList = ({ children, filterDefaultValues, ...props }) => {
  const activeId = useActiveDbVersionId();
  if (!activeId) return null;
  return (
    <List
      {...props}
      filterDefaultValues={{
        databaseVersionId: activeId,
        ...(filterDefaultValues || {}),
      }}
    >
      {children}
    </List>
  );
};

// ─── Bcome Version (Version Set) utilities ────────────────────────────────────

export const useActiveBcomeVersionId = () => {
  const tokenOps = useTokenOps();
  const [activeId, setActiveId] = useState(() =>
    localStorage.getItem(ACTIVE_BCOME_VERSION_STORAGE_KEY)
  );

  useEffect(() => {
    const token = tokenOps.account?.accessToken;
    if (!token) return;
    // TODO: backend ignores `filter=isActive||$eq||true` on /bcome-version
    // (returns all records). Workaround: fetch all and pick isActive=true in JS.
    // Remove when backend exposes isActive as a filterable field.
    axios
      .get(`${hostname}/bcome-version`, {
        params: { limit: 200 },
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + token,
        },
      })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.data;
        const active = list && list.find((r) => r.isActive === true);
        const id = active && active.id;
        if (id) {
          setActiveId(id);
          localStorage.setItem(ACTIVE_BCOME_VERSION_STORAGE_KEY, id);
        } else {
          console.warn("[BcomeVersion] no Version Set with isActive=true found");
        }
      })
      .catch((e) => console.log("[BcomeVersion] active fetch failed", e));
  }, [tokenOps.account?.accessToken]);

  return activeId;
};

export const useBcomeVersionRecordMap = () => {
  const tokenOps = useTokenOps();
  const [map, setMap] = useState({});

  useEffect(() => {
    const token = tokenOps.account?.accessToken;
    if (!token) return;
    axios
      .get(`${hostname}/bcome-version`, {
        params: { limit: 200 },
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + token,
        },
      })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.data;
        if (!list) return;
        const next = {};
        list.forEach((r) => {
          next[r.id] = r;
        });
        setMap(next);
      })
      .catch((e) => console.log("[BcomeVersion] record map fetch failed", e));
  }, [tokenOps.account?.accessToken]);

  return map;
};

export const useBcomeVersionMap = () => {
  const tokenOps = useTokenOps();
  const [map, setMap] = useState({});

  useEffect(() => {
    const token = tokenOps.account?.accessToken;
    if (!token) return;
    axios
      .get(`${hostname}/bcome-version`, {
        params: { limit: 200 },
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + token,
        },
      })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.data;
        if (!list) return;
        const next = {};
        list.forEach((r) => {
          next[r.id] = formatBcomeVersionLabel(r);
        });
        setMap(next);
      })
      .catch((e) => console.log("[BcomeVersion] map fetch failed", e));
  }, [tokenOps.account?.accessToken]);

  return map;
};

// Same as useBcomeVersionMap but only Active=true Version Sets. Used to
// populate the upload selector, so inactive versions can't be chosen.
// (Labels of already-saved records must still resolve inactive ids, so those
// consumers use the unfiltered useBcomeVersionMap above.)
// NOTE: backend ignores `filter=isActive||$eq||true` on /bcome-version, hence
// the JS-side filter — same workaround as useActiveBcomeVersionId.
export const useActiveBcomeVersionMap = () => {
  const tokenOps = useTokenOps();
  const [map, setMap] = useState({});

  useEffect(() => {
    const token = tokenOps.account?.accessToken;
    if (!token) return;
    axios
      .get(`${hostname}/bcome-version`, {
        params: { limit: 200 },
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + token,
        },
      })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.data;
        if (!list) return;
        const next = {};
        list
          .filter((r) => r.isActive === true)
          .forEach((r) => {
            next[r.id] = formatBcomeVersionLabel(r);
          });
        setMap(next);
      })
      .catch((e) =>
        console.log("[BcomeVersion] active map fetch failed", e)
      );
  }, [tokenOps.account?.accessToken]);

  return map;
};

export const useAccountBcomeVersions = () => {
  const tokenOps = useTokenOps();
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    const token = tokenOps.account?.accessToken;
    if (!token) return;
    axios
      .get(`${hostname}/bcome-version/byAccount`, {
        headers: {
          "Content-type": "application/json",
          authorization: "Bearer " + token,
        },
      })
      .then(({ data }) => {
        console.log("[BcomeVersion] /byAccount response:", data);
        const list = Array.isArray(data) ? data : data?.data;
        if (Array.isArray(list)) setVersions(list);
      })
      .catch((e) => console.log("[BcomeVersion] /byAccount fetch failed", e));
  }, [tokenOps.account?.accessToken]);

  return versions;
};

export const BcomeVersionFilterInput = (props) => {
  const versions = useAccountBcomeVersions();
  const choices = versions.map((v) => ({
    id: v.id,
    name: formatBcomeVersionLabel(v),
  }));
  return (
    <SelectInput
      {...props}
      label="Version Set"
      source="bcomeVersionId"
      choices={choices}
      allowEmpty
    />
  );
};

export const BcomeVersionedList = ({
  children,
  filterDefaultValues,
  ...props
}) => {
  const activeId = useActiveBcomeVersionId();
  if (!activeId) return null;
  return (
    <List
      {...props}
      filterDefaultValues={{
        bcomeVersionId: activeId,
        ...(filterDefaultValues || {}),
      }}
    >
      {children}
    </List>
  );
};

export default VersionedList;
